import React from 'react';
import {
	Document,
	Page,
	Text,
	View,
	StyleSheet,
	Image,
	Font,
} from '@react-pdf/renderer';
import AssemblieLogo from '../../assets/Icon_Primary.png';

const PlanPdfTemplate = ({ plan, orgData }) => {
	const styles = StyleSheet.create({
		page: {
			padding: 40,
			fontFamily: 'Helvetica',
			position: 'relative', // For footer positioning
		},
		header: {
			flexDirection: 'row',
			alignItems: 'center',
			backgroundColor: orgData.secondaryColor,
			padding: 12, // Reduced from 16
			justifyContent: 'space-between',
			marginBottom: 16, // Reduced from 20
		},
		contentWrapper: {
			flex: 1,
			paddingBottom: 40, // Reduced from 60
		},
		orgLogo: {
			width: 50,
			height: 50,
			marginRight: 15,
			backgroundColor: '#FAFAFA',
			padding: 4,
			borderRadius: 25,
		},
		orgName: {
			fontSize: 20,
			fontWeight: 'bold',
		},
		planTitle: {
			fontSize: 20,
			textAlign: 'center',
		},
		section: {
			borderRight: '1 solid #eee',
			borderLeft: '1 solid #eee',
			marginBottom: 12, // Reduced from 20
			breakInside: 'avoid', // Try to keep sections together
		},
		sectionHeader: {
			backgroundColor: orgData.secondaryColor,
			padding: 8,
			marginBottom: 6, // Reduced from 8
			breakInside: 'avoid', // Keep header with first block
		},
		sectionTitle: {
			fontSize: 14,
			fontWeight: 'bold',
		},
		sectionDescription: {
			fontSize: 10,
			marginVertical: 8,
			color: 'white',
		},
		block: {
			padding: 8,
			borderBottom: '1 solid #eee',
			breakInside: 'avoid', // Try to keep blocks together
		},
		blockHeader: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			marginBottom: 5,
		},
		blockTitle: {
			fontSize: 14,
			fontWeight: 'bold',
		},
		blockSongKey: {
			fontSize: 14,
			fontWeight: 'bold',
			color: '#666',
		},
		blockContent: {
			fontSize: 10,
			marginTop: 4, // Add some spacing between title and content
			marginBottom: 4,
		},
		blockNotes: {
			fontSize: 10,
			fontStyle: 'italic',
			color: '#666',
		},
		footer: {
			position: 'absolute',
			bottom: 20, // Reduced from 30
			left: 40,
			right: 40,
			textAlign: 'center',
			flexDirection: 'row',
			justifyContent: 'center',
			alignItems: 'center',
			fontSize: 10,
			color: '#666',
			borderTop: '1 solid #eee',
			paddingTop: 8, // Reduced from 10
		},
		footerLogo: {
			width: 40,
			height: 40,
			marginRight: 5,
		},
		mediaSection: {
			marginTop: 4,
			borderTop: '1 solid #eee',
			paddingTop: 4,
		},
		mediaItem: {
			flexDirection: 'row',
			alignItems: 'center',
			fontSize: 9,
			color: '#666',
			marginBottom: 2,
		},
		mediaIcon: {
			fontSize: 9,
			marginRight: 4,
			fontFamily: 'Helvetica',
		},
		mediaName: {
			fontSize: 9,
			color: '#666',
			flex: 1,
		},
		teamSection: {
			marginTop: 8, // Reduced from 10
			marginBottom: 16, // Reduced from 20
			padding: 10, // Reduced from 12
			backgroundColor: '#f5f5f5',
			breakInside: 'avoid',
		},
		teamHeader: {
			fontSize: 14,
			fontWeight: 'bold',
			marginBottom: 12,
			color: '#333',
		},
		teamMember: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			marginBottom: 6,
			padding: 4,
		},
		memberName: {
			fontSize: 10,
			color: '#333',
		},
		memberRole: {
			fontSize: 10,
			color: '#666',
		},
		blockLabel: {
			fontSize: 8,
			color: '#666',
			marginBottom: 2,
			fontStyle: 'italic',
		},
		pageNumber: {
			position: 'absolute',
			bottom: 20, // Reduced from 30
			right: 40,
			fontSize: 10,
			color: '#666',
		},
	});

	// Helper function to get user role
	const getUserRole = (user) => {
		return user.PlanUser?.role || user.role || '';
	};

	// Updated helper function to return simple text icons
	const getMediaTypeText = (fileType) => {
		if (!fileType) return '[File]';
		if (fileType.startsWith('image/')) return '[Image]';
		if (fileType.startsWith('video/')) return '[Video]';
		if (fileType.startsWith('audio/')) return '[Audio]';
		return '[File]';
	};

	return (
		<Document>
			<Page
				size='A4'
				style={styles.page}>
				{/* Fixed Header */}
				<View style={styles.header}>
					<View
						style={{
							flexDirection: 'row',
							justifyContent: 'center',
							alignItems: 'center',
						}}>
						{orgData.orgPicture && (
							<Image
								style={styles.orgLogo}
								src={{ uri: orgData.orgPicture }}
							/>
						)}
						<Text style={styles.orgName}>{orgData.name}</Text>
					</View>
					<Text style={styles.planTitle}>{plan.mainTitle}</Text>
				</View>

				{/* Scrollable Content */}
				<View style={styles.contentWrapper}>
					{/* Team Members Section */}
					{plan.teamMembers && plan.teamMembers.length > 0 && (
						<View style={styles.teamSection}>
							<Text style={styles.teamHeader}>Team Members</Text>
							{plan.teamMembers.map((member) => (
								<View
									key={member.id}
									style={styles.teamMember}>
									<Text style={styles.memberName}>
										{member.firstName} {member.lastName}
									</Text>
									{plan.teamMemberRoles?.find(
										(tmr) => tmr.userId === member.id
									) && (
										<Text style={styles.memberRole}>
											{
												plan.teamMemberRoles.find(
													(tmr) =>
														tmr.userId === member.id
												).teamRole.name
											}
											{plan.teamMemberRoles.find(
												(tmr) =>
													tmr.userId === member.id
											).notes &&
												` (${
													plan.teamMemberRoles.find(
														(tmr) =>
															tmr.userId ===
															member.id
													).notes
												})`}
										</Text>
									)}
								</View>
							))}
						</View>
					)}

					{/* Plan Sections */}
					{plan.sections?.map((section) => (
						<View
							key={section.id}
							style={styles.section}
							wrap={false}>
							<View style={styles.sectionHeader}>
								<Text style={styles.sectionTitle}>
									{section.title} ({section.estimatedDuration}{' '}
									min)
								</Text>
								{section.description && (
									<Text style={styles.sectionDescription}>
										{section.description}
									</Text>
								)}
							</View>

							{section.blocks?.map((block) => (
								<View
									key={block.id}
									style={styles.block}
									wrap={false}>
									<View style={styles.blockHeader}>
										<Text style={styles.blockTitle}>
											{block.type === 'worship'
												? `${block.songName}${
														block.songKey ? ' ' : ''
												  }`
												: block.title}
											{block.type === 'worship' &&
												block.songKey && (
													<Text
														style={
															styles.blockSongKey
														}>
														({block.songKey})
													</Text>
												)}
										</Text>
									</View>
									{block.content && (
										<Text style={styles.blockContent}>
											{block.content}
										</Text>
									)}
									{block.notes && (
										<>
											<Text style={styles.blockLabel}>
												Notes:
											</Text>
											<Text style={styles.blockNotes}>
												{block.notes}
											</Text>
										</>
									)}

									{/* Updated Media Section */}
									{block.media && (
										<View style={styles.mediaSection}>
											<Text style={styles.blockLabel}>
												Media:
											</Text>
											{Array.isArray(block.media) ? (
												block.media.map((media) => (
													<View
														key={media.id}
														style={
															styles.mediaItem
														}>
														<Text
															style={
																styles.mediaIcon
															}>
															{getMediaTypeText(
																media.fileType
															)}
														</Text>
														<Text
															style={
																styles.mediaName
															}>
															{media.name}
														</Text>
													</View>
												))
											) : (
												<View style={styles.mediaItem}>
													<Text
														style={
															styles.mediaIcon
														}>
														{getMediaTypeText(
															block.media.fileType
														)}
													</Text>
													<Text
														style={
															styles.mediaName
														}>
														{block.media.name}
													</Text>
												</View>
											)}
										</View>
									)}
								</View>
							))}
						</View>
					))}
				</View>

				{/* Fixed Footer */}
				<View
					style={styles.footer}
					fixed>
					<Image
						style={styles.footerLogo}
						src={AssemblieLogo}
					/>
					<Text>Created by Assemblie</Text>
				</View>

				{/* Page Numbers */}
				<Text
					style={styles.pageNumber}
					render={({ pageNumber, totalPages }) =>
						`${pageNumber} / ${totalPages}`
					}
					fixed
				/>
			</Page>
		</Document>
	);
};

export default PlanPdfTemplate;
